<script setup lang="ts">
import Button from '../Button/index.vue'
import type { ButtonVariants } from '../Button/data'

const props = withDefaults(
    defineProps<{
        arrow?: boolean
        loading?: boolean
        disabled?: boolean
        variant?: ButtonVariants['variant']
        block?: boolean
        to?: string
    }>(),
    {
        arrow: false,
        loading: false,
        disabled: false,
        variant: 'primary',
        block: false,
    },
)

const slots = useSlots()

const buttonStyles = computed(() => [
    { 'justify-between': props.arrow },
    { 'pl-6 pr-1.5': slots['icon-right'] || (props.variant !== 'linkDark' && props.variant !== 'link' && props.arrow) },
    { 'pl-1.5 pr-6': slots['icon-left'] },
    { 'disabled:border-plus-lp-dark-blue-400': props.variant === 'secondary' && props.loading },
    { 'disabled:border-plus-lp-grey-200': props.variant === 'tertiary' && props.loading },
    {
        '*:invisible before:absolute before:inset-x-0 before:mx-auto before:size-7 before:animate-spin disabled:text-transparent':
            props.loading,
    },
    { 'disabled:bg-plus-lp-green-one-disabled': props.variant === 'primaryDark' && props.loading },
    { 'disabled:bg-plus-lp-dark-blue-400': props.loading && props.variant === 'primary' },
    { 'disabled:bg-plus-lp-white-disabled-bg': props.loading && props.variant === 'secondaryDark' },
    {
        'disabled:bg-plus-lp-transparent':
            props.loading && (props.variant === 'tertiary' || props.variant === 'secondary'),
    },
    { 'w-full': props.block },
])
</script>

<template>
    <Button
        :disabled="disabled || loading"
        :variant="variant"
        :to="to"
        :class="buttonStyles"
    >
        <div
            v-if="$slots['icon-left']"
            class="mr-2 flex size-8 items-center justify-center rounded-full bg-plus-lp-grey-200 group-disabled:opacity-50 group-disabled:grayscale"
        >
            <slot name="icon-left" />
        </div>
        <span
            :class="[
                {
                    'relative before:absolute before:bottom-0 before:left-0 before:h-px before:w-full before:scale-x-100 before:animate-lp-slide-in group-hover:before:animate-lp-slide-out group-disabled:before:animate-none':
                        (variant as string)?.startsWith('link'),
                },
                {
                    'before:bg-plus-lp-grey-100  group-disabled:before:bg-plus-lp-white-disabled-text':
                        variant === 'linkDark',
                },
                {
                    'before:bg-plus-lp-dark-blue group-disabled:before:bg-plus-lp-dark-blue-disabled-text':
                        variant === 'link',
                },
                { 'w-full': block },
            ]"
        >
            <slot />
        </span>
        <div
            v-if="$slots['icon-right']"
            class="ml-2 flex size-8 items-center justify-center group-disabled:opacity-50 group-disabled:grayscale"
        >
            <slot name="icon-right" />
        </div>
        <div
            v-if="arrow"
            class="flex min-h-8 min-w-8 items-center justify-center rounded-full"
            :class="[
                variant === 'secondaryDark'
                    ? 'group-disabled:!bg-plus-lp-white-disabled-bg'
                    : 'group-disabled:bg-plus-lp-dark-blue-disabled-bg',
                variant === 'secondary' || variant === 'primaryDark' ? 'bg-plus-lp-dark-blue' : 'bg-plus-lp-green-one',
                {
                    'bg-plus-lp-transparent group-disabled:!bg-plus-lp-transparent !ml-0': (
                        variant as string
                    )?.startsWith('link'),
                },
                { 'ml-5': arrow && !block },
            ]"
        >
            <svg
                class="size-5"
                :class="[
                    variant === 'secondaryDark' || variant === 'linkDark'
                        ? 'group-disabled:fill-white-disabled-text'
                        : 'group-disabled:fill-plus-lp-dark-blue-disabled-text',
                    { 'fill-plus-lp-dark-blue': variant === 'secondaryDark' },
                    { 'fill-white': variant === 'linkDark' },
                    { 'fill-plus-lp-green-one': variant === 'primaryDark' },
                    {
                        'transition-transform duration-300 ease-in-out group-hover:scale-[1.2] group-active:scale-100 group-disabled:scale-100':
                            !(variant as string)?.startsWith('link'),
                    },
                ]"
                :fill="variant === 'secondary' ? 'white' : 'dark-blue'"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10.244 14.7559C9.9186 14.4305 9.9186 13.9028 10.244 13.5774L13.8214 10L10.244 6.42259C9.9186 6.09715 9.9186 5.56951 10.244 5.24408C10.5695 4.91864 11.0971 4.91864 11.4225 5.24408L15.5892 9.41074C15.9147 9.73618 15.9147 10.2638 15.5892 10.5893L11.4225 14.7559C11.0971 15.0814 10.5695 15.0814 10.244 14.7559Z"
                />
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.16663 10C4.16663 9.53976 4.53972 9.16667 4.99996 9.16667H15C15.4602 9.16667 15.8333 9.53976 15.8333 10C15.8333 10.4602 15.4602 10.8333 15 10.8333H4.99996C4.53972 10.8333 4.16663 10.4602 4.16663 10Z"
                />
            </svg>
        </div>
        <img
            v-if="
                loading &&
                    (props.variant === 'primary' || props.variant === 'secondaryDark' || props.variant === 'primaryDark')
            "
            src="/assets/icons/loader-dark.png"
            alt=""
            class="!visible absolute inset-x-0 mx-auto animate-spin"
        />
        <img
            v-else-if="loading"
            src="/assets/icons/loader-light.png"
            alt=""
            class="!visible absolute inset-x-0 mx-auto animate-spin"
        />
    </Button>
</template>
